import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../../../contexts/ToastContext';
import useCreateTeamGroup from '../../../../../hooks/api/teams/teamGroups/useCreateTeamGroup';
import useInputValue from '../../../../../hooks/useInputValue';
import { getErrorMessage } from '../../../../../ReactQuery/errors';
import Button from '../../../../generic/Button/Button';
import ButtonGroup from '../../../../generic/Button/ButtonGroup';
import LoaderButton from '../../../../generic/Button/LoaderButton';
import FormGroup from '../../../../generic/Forms/FormGroup';
import FormLabel from '../../../../generic/Forms/FormLabel';
import LabeledTextInput from '../../../../generic/Forms/LabeledTextInput';
import Select from '../../../../generic/Select/Select';
import useSelectRoles from '../hooks/useSelectRoles';

import styles from './TeamGroups.module.less';

type Props = {
  isOpen: boolean;
  onHide: () => void;
};

/**
 * This is the modal for creating a new TeamGroup.
 *
 * @param {Props} props - The props for the component.
 * @param {boolean} props.isOpen - Whether the modal is open.
 * @param {() => void} props.onHide - The function to call when the modal is hidden.
 */
const CreateTeamGroupModal: VoidFunctionComponent<Props> = ({ isOpen, onHide }) => {
  const addToast = useAddToast();
  const [teamGroupName, onChangeTeamGroupName] = useInputValue();

  //  Role options
  const {
    roleOptions,
    isLoading: isLoadingRoles,
    selectedRole,
    onChangeRole,
  } = useSelectRoles();

  //  Mutation to create a team group.
  //  Note that we pass in the new team group as an object
  //  so that we can use the same mutation for both creating a team group,
  //  and then updating the list of team groups.
  const {
    mutateAsync: createTeamGroup,
    isLoading: isCreatingTeamGroup,
    reset: resetCreateTeamGroupError,
  } = useCreateTeamGroup({
    name: teamGroupName,
    defaultRoleId: selectedRole?.value ?? 0,
  });

  //  Clear the form and hide the modal
  const clearFormAndHideModal = () => {
    onChangeTeamGroupName('');
    onChangeRole(null);
    resetCreateTeamGroupError();
    onHide();
  };

  //  Create the team group, but catch any errors and display them
  //  instead of showing the error in the modal
  const handleCreateTeamGroup = async () => {
    //  Don't allow the user to create a team group if the form is invalid
    if (!teamGroupName && !selectedRole) {
      return;
    }

    try {
      await createTeamGroup();
      clearFormAndHideModal();
      addToast({
        type: 'success',
        content: `Team group "${teamGroupName}" created successfully.`,
      });
    } catch (error: unknown) {
      addToast({
        type: 'error',
        content: getErrorMessage(error, 'Failed to create team group.'),
      });
    }
  };

  //  Disable the save button if the form is invalid or if there is an async operation in progress.
  const isSaveButtonDisabled = !teamGroupName
    || !selectedRole
    || isCreatingTeamGroup
    || isLoadingRoles;

  return (
    <Modal show={isOpen} onHide={onHide} animation>
      <Modal.Header closeButton>
        <Modal.Title>
          New Team Group
        </Modal.Title>
        <div className={styles.createTeamGroupDescription}>
          Create a group of teams
        </div>
      </Modal.Header>
      <Modal.Body className="margin-top-md">
        <FormGroup>
          <LabeledTextInput
            label="Group Name"
            value={teamGroupName}
            onChange={onChangeTeamGroupName}
            size="small"
            placeholder="Enter a name for this team"
          />
          <FormLabel label="Default Group Role">
            <Select
              id="team-group-default-role-select"
              options={roleOptions}
              value={selectedRole}
              onChange={onChangeRole}
              isDisabled={isLoadingRoles}
              isLoading={isLoadingRoles}
            />
          </FormLabel>
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
          >
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            onClick={handleCreateTeamGroup}
            isDisabled={isSaveButtonDisabled}
            isLoading={isCreatingTeamGroup}
          >
            Create
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateTeamGroupModal;
